/**
 * The New organization wizard's own rules, kept pure so they can be tested
 * without a DOM: what each step must hold before Next, the body the Review
 * step posts, and the sentence the tasks tray says when the create lands.
 */
import { slugSchema, type OnboardOrgRequest, type OnboardResult } from '@simpleacs/shared';

export interface OrgStep {
  name: string;
  slug: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

export interface AdminStep {
  name: string;
  email: string;
  phone: string;
  password: string;
}

export interface WelcomeChoice {
  email: boolean;
  includePassword: boolean;
}

export const EMPTY_ORG_STEP: OrgStep = { name: '', slug: '', contactName: '', contactEmail: '', contactPhone: '' };
export const EMPTY_ADMIN_STEP: AdminStep = { name: '', email: '', phone: '', password: '' };

/** The API's minimum, restated here so the step can refuse before the round trip. */
export const PASSWORD_MIN_LENGTH = 10;

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** The slug the org will get: the typed one, else the name's. */
export function effectiveSlug(step: OrgStep): string {
  return step.slug.trim() || slugify(step.name);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function orgStepProblems(step: OrgStep): string[] {
  const problems: string[] = [];
  if (step.name.trim().length < 2) problems.push('Give the organization a name of at least two characters.');
  const slug = effectiveSlug(step);
  const verdict = slugSchema.safeParse(slug);
  if (!verdict.success) {
    problems.push(
      slug === ''
        ? 'The slug is empty — type one, or a name it can be made from.'
        : `"${slug}" is not a usable slug: lowercase letters, digits and single dashes only, 2 to 40 characters.`,
    );
  }
  const contactEmail = step.contactEmail.trim();
  if (contactEmail && !EMAIL_RE.test(contactEmail)) problems.push(`"${contactEmail}" is not an email address.`);
  return problems;
}

export function adminStepProblems(step: AdminStep): string[] {
  const problems: string[] = [];
  if (step.name.trim() === '') problems.push("Give the administrator's name.");
  const email = step.email.trim();
  if (!EMAIL_RE.test(email)) problems.push(email ? `"${email}" is not an email address.` : 'The administrator needs an email address — it is their username.');
  if (step.password.length < PASSWORD_MIN_LENGTH) {
    problems.push(`The password needs at least ${PASSWORD_MIN_LENGTH} characters — Generate makes one.`);
  }
  return problems;
}

function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** What Review posts (dryRun) and what Create posts (the same, live). */
export function onboardingBody(
  org: OrgStep,
  admin: AdminStep,
  welcome: WelcomeChoice,
  dryRun: boolean,
): OnboardOrgRequest {
  return {
    org: {
      name: org.name.trim(),
      slug: effectiveSlug(org),
      contactName: orNull(org.contactName),
      contactEmail: orNull(org.contactEmail),
      contactPhone: orNull(org.contactPhone),
    },
    admin: {
      name: admin.name.trim(),
      email: admin.email.trim().toLowerCase(),
      phone: orNull(admin.phone),
      password: admin.password,
    },
    welcome: { email: welcome.email, includePassword: welcome.email && welcome.includePassword },
    dryRun,
  };
}

/** The tray's sentence once the create has landed. */
export function onboardingSummary(result: OnboardResult): string {
  const org = result.org;
  const admin = result.admin;
  const head = org
    ? `${org.name}${org.number ? ` (#${org.number})` : ''} is created`
    : 'The organization is created';
  const who = admin ? `; ${admin.email} is its administrator.` : '.';
  const mail = result.welcome ? ` ${result.welcome.note}` : '';
  return `${head}${who}${mail}`;
}
